import { supabase } from './supabase'

// ── The Final Cut card ────────────────────────────────────────────────────────
// One card per finished show. Not a tier, not a pack, not a rarity: the thing
// that's left when the wrap cinematic is over. It carries what only YOU could
// put on it — your rating, your run (when you started, how many nights it took),
// your one-line take — so no two people's card for the same show match.
// (Jason 2026-09-20: "at least one memorable achievable item.")
//
// Store: series_keepsakes, one row per (user, show), RLS own-write / own-or-
// followed-read. Minted from useWatchlist.setStatus on EVERY completion (bulk
// imports too, quietly, with the source's historical date), so the shelf is the
// full record of finished shows, not just the ones finished after this shipped.
// The SQL backfill in the 2026-09-20 migration covers everything before.

const SELECT = 'id, show_id, show_name, poster_path, backdrop_path, seasons, episodes, runtime_minutes, rating, completed_at, started_at, nights, take'

export async function fetchKeepsakes(userId) {
  if (!userId) return []
  const { data } = await supabase.from('series_keepsakes').select(SELECT)
    .eq('user_id', userId).order('completed_at', { ascending: true })
  return data || []
}

// Card numbers are the order you finished shows in (No. 1 is your first ever),
// derived from completed_at so a backfilled shelf numbers itself honestly.
export function numberKeepsakes(list) {
  return [...list].sort((a, b) => new Date(a.completed_at) - new Date(b.completed_at)).map((k, i) => ({ ...k, no: i + 1 }))
}

// Distinct days on which the member marked an episode of this show — "nights".
async function countNights(userId, showId) {
  const { data } = await supabase.from('activities').select('created_at')
    .eq('user_id', userId).eq('show_id', showId).eq('type', 'episode').limit(2000)
  const days = new Set((data || []).map(a => String(a.created_at).slice(0, 10)))
  return days.size
}

// The member's latest top-level comment on the show — the take. Reads the
// spoiler-gated table through the same RLS the composer writes under, so it
// only ever returns the member's OWN words.
async function latestTake(userId, showId) {
  const { data } = await supabase.from('show_comments').select('body')
    .eq('user_id', userId).eq('show_id', showId).is('parent_id', null)
    .order('created_at', { ascending: false }).limit(1)
  return data?.[0]?.body || null
}

// Mint (or complete) the card for a finished show. Idempotent on (user, show):
// a second completion of the same show keeps the ORIGINAL completed_at and only
// fills fields that are still empty, so re-marking a show done never rewrites
// its history. Returns the numbered card, or null.
export async function mintKeepsake({ user, show, rating = null, completedAt = null, bulk = false }) {
  if (!user?.id || !show?.id) return null
  const userId = user.id
  const segs = Array.isArray(show.seasons) ? show.seasons.filter(s => s.season_number > 0) : []
  const seasons = show.number_of_seasons || segs.length || null
  const episodes = show.number_of_episodes || (segs.length ? segs.reduce((n, s) => n + (s.episode_count || 0), 0) : null) || null
  const runtime = Array.isArray(show.episode_run_time) ? show.episode_run_time[0] : null
  const runtime_minutes = episodes && runtime ? episodes * runtime : null

  let started_at = null
  try {
    const { data } = await supabase.from('watchlist').select('added_at').eq('user_id', userId).eq('show_id', show.id).maybeSingle()
    started_at = data?.added_at || null
  } catch { /* best-effort */ }
  const [nights, take] = bulk ? [null, null] : await Promise.all([countNights(userId, show.id).catch(() => null), latestTake(userId, show.id).catch(() => null)])

  const { data: existing } = await supabase.from('series_keepsakes').select(SELECT).eq('user_id', userId).eq('show_id', show.id).maybeSingle()
  const row = {
    user_id: userId, show_id: show.id, show_name: show.name,
    poster_path: existing?.poster_path || show.poster_path || null,
    backdrop_path: existing?.backdrop_path || show.backdrop_path || null,
    seasons: existing?.seasons ?? seasons, episodes: existing?.episodes ?? episodes,
    runtime_minutes: existing?.runtime_minutes ?? runtime_minutes,
    rating: existing?.rating ?? (rating ? Math.round(rating * 2) / 2 : null),
    completed_at: existing?.completed_at || completedAt || new Date().toISOString(),
    started_at: existing?.started_at || started_at,
    nights: existing?.nights ?? nights,
    take: existing?.take || take,
  }
  const { data, error } = await supabase.from('series_keepsakes').upsert(row, { onConflict: 'user_id,show_id' }).select(SELECT).single()
  if (error) return null
  const saved = data || { ...row }
  const { count } = await supabase.from('series_keepsakes').select('id', { count: 'exact', head: true })
    .eq('user_id', userId).lte('completed_at', saved.completed_at)
  return { ...saved, no: count || 1, isNew: !existing }
}

// Fill in a rating on an existing card (the Final Cut cinematic's series rating
// lands after the mint). Never creates a row.
export async function rateKeepsake(userId, showId, rating) {
  if (!userId || !showId || !rating) return
  await supabase.from('series_keepsakes').update({ rating: Math.round(rating * 2) / 2 }).eq('user_id', userId).eq('show_id', showId)
}

// The take, written on the card itself from the reveal sheet ("One line for the
// card?") — the moment people most want to say something. Own row only (RLS).
export async function saveTake(userId, showId, take) {
  if (!userId || !showId) return
  const t = (take || '').trim().slice(0, 140) || null
  await supabase.from('series_keepsakes').update({ take: t }).eq('user_id', userId).eq('show_id', showId)
  return t
}

// ── Copy ─────────────────────────────────────────────────────────────────────
const dayMs = 86400000
export function runLine(k) {
  const started = k?.started_at ? new Date(k.started_at) : null
  const finished = k?.completed_at ? new Date(k.completed_at) : null
  const days = started && finished ? Math.max(1, Math.round((finished - started) / dayMs)) : null
  const nights = k?.nights || 0
  const eps = k?.episodes || 0
  if (days == null) return nights > 1 ? `${nights} nights` : null
  if (nights >= 1 && nights <= 2 && eps >= 4) return nights === 1 ? 'Binged in one night' : 'Binged in two nights'
  if (days <= 7) return days === 1 ? 'Done in a day' : `Done in ${days} days`
  if (days >= 540) { const y = days / 365; return `A ${y >= 2 ? Math.round(y) : 'year-and-a-half'}${y >= 2 ? '-year' : ''} slow burn` }
  const unit = days >= 60 ? `${Math.round(days / 30)} months` : `${Math.max(2, Math.round(days / 7))} weeks`
  return nights > 1 ? `${nights} nights across ${unit}` : `${unit} with it`
}

export function hoursLine(k) {
  const m = k?.runtime_minutes
  if (!m) return k?.episodes ? `${k.episodes} episodes` : null
  const h = Math.round(m / 60)
  return h < 1 ? `${m} minutes` : `about ${h} hour${h === 1 ? '' : 's'}`
}

export function fmtShort(iso) {
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) } catch { return '' }
}
